<template>
  <div class="the-header">
    <div class="the-header__sticky-line">
      <div class="container">
        <TheHeaderLine
          :site-logo="siteLogo"
          :without-analyses-cart="withoutAnalysesCart"
          :analyses-count-in-cart="analysesCountInCart"
          data-qa="the_header_line"
          @header:click-hamburger-btn="handleClickHamburger"
        />
        <CoreSearch
          class="mt-2"
          @click-search-btn="handleShowDialog"
        />
      </div>
    </div>

    <SearchDialog
      v-model="isVisibleSearchDialog"
    />

    <SideMenu
      v-model="isSideMenuActive"
      :expert-url="expertUrl"
      :available-languages="availableLanguages"
      :town="transDynamic(searchData.location.town, `name,${language},${needTranslate}`)"
      :town-select-url="townSelectUrl"
      :projects="projects"
      :personal-area-url="personalAreaUrl"
    />
  </div>
</template>

<script setup lang="ts">
import {
  computed,
  onMounted,
  ref,
  toRefs,
  type Ref,
} from 'vue'
import I18NextVue from 'i18next'
import { useContextStore } from 'src/store'
import transDynamic from 'djanjucks/filters/transDynamic'
import type { ContextStore } from 'src/components/pages/info/about-us/index/types'
import makeElementSticky from 'www/utils/withDependences/makeElementSticky'
import CoreSearch from 'src/layouts/core-layout/components/CoreSearch/CoreSearch.vue'
import TheHeaderLine from 'src/layouts/core-layout/components/CoreHeader/components/mobile/TheHeader/TheHeaderLine.vue'
import SideMenu from 'src/layouts/core-layout/components/CoreHeader/components/mobile/SideMenu/SideMenu.vue'
import SearchDialog from 'src/layouts/core-layout/components/CoreSearch/components/SearchDialog.vue'
import { getSiteLogoPd, type SiteLogo } from 'src/layouts/core-layout/components/CoreHeader/components/mobile/TheHeader/constants'
import type { SideMenuLanguage } from 'src/layouts/core-layout/components/CoreHeader/components/mobile/SideMenu/SideMenu.types'
import type { SideMenuProject } from 'src/layouts/core-layout/components/CoreHeader/components/mobile/TheHeader/TheHeader.types'
import type { TheHeaderMobileSearchData } from 'src/layouts/core-layout/components/CoreHeader/components/mobile/TheHeader/TheHeaderMobile.types'

const props = withDefaults(defineProps<{
  siteLogo?: SiteLogo
  search?: TheHeaderMobileSearchData
  withoutAnalysesCart?: boolean
  analysesCountInCart?: number
  expertUrl?: string
  availableLanguages?: SideMenuLanguage[]
  townSelectUrl?: string
  projects?: Record<string, SideMenuProject>
  personalAreaUrl?: string
}>(), {
  siteLogo: () => getSiteLogoPd(),
  search: () => ({}),
  withoutAnalysesCart: false,
  analysesCountInCart: 0,
  expertUrl: '',
  availableLanguages: () => [],
  townSelectUrl: '',
  projects: undefined,
  personalAreaUrl: '',
})

const {
  siteLogo,
  withoutAnalysesCart,
  analysesCountInCart,
  expertUrl,
  availableLanguages,
  townSelectUrl,
  projects,
  personalAreaUrl,
} = toRefs(props)

const isSideMenuActive: Ref<boolean> = ref(false)
const isVisibleSearchDialog: Ref<boolean> = ref(false)

const searchData = computed(() => ({
  ...props.search,
  location: {
    ...(props.search?.location || {}),
  },
}))

const { context }: ContextStore = useContextStore()
const { language } = I18NextVue
const needTranslate = context.feature_flags.pd_l10n_footer_pages

onMounted(() => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return
  }

  const filtersElement = document.querySelector('.vue-the-doctors-filters')

  makeElementSticky({
    el: '.the-header__sticky-line',
    topLimitInvisible: filtersElement instanceof HTMLElement
      ? filtersElement.offsetTop + filtersElement.clientHeight
      : 0,
  })
})

function handleClickHamburger() {
  isSideMenuActive.value = true
}

function handleShowDialog() {
  isVisibleSearchDialog.value = true
}
</script>

<style lang="scss" scoped>
@import '~www/themes/doctors/common/variables';

.the-header {
  position: relative;
  min-height: 96px;
  background-color: $ui-kit-bg-gray-0;

  &__sticky-line {
    transition: opacity .3s ease, transform .3s ease, box-shadow .3s ease;
    transform: translateY(0);
    opacity: 1;
    padding: 8px 0;
    background-color: $ui-kit-bg-gray-0;

    &.invisible {
      opacity: 0;
      transform: translateY(-100%);
    }

    &.sticky {
      position: fixed;
      top: 0;
      right: 0;
      left: 0;
      z-index: 100;

      @include new-elevation-1;

      .container {
        width: calc(100% - 16px);
        max-width: 1120px;
        margin: 0 auto;
      }
    }
  }
}
</style>
